/**
 * Where the host is (client-app §6). A page the host serves talks to /api on
 * its own origin and needs nothing from here. The packaged app (client-app
 * §8) is served from its own bundle on the phone, so it holds the host's
 * address instead: typed once on the connect screen, kept on the phone, and
 * read once at start by everything that reaches the host.
 */

/** Where the phone keeps the host's address. */
export const HOST_KEY = "ark-bot.host";

/** The hosted product: what the bundled shell's connect screen offers first, and what the shipped shell loads outright (client-app/capacitor). */
export const HOSTED_APP = "https://bot.arkmagus.com";

/**
 * What a person types, as an origin: a scheme, a name and a port, nothing
 * after. A bare name is taken as https, the only kind a phone installs from
 * (client-app §5.1); a pasted page address still names its host. Null when
 * it is not an address at all.
 */
export function normaliseHost(input: string): string | null {
  const trimmed = input.trim();
  if (trimmed.length === 0) return null;
  const withScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  let url: URL;
  try {
    url = new URL(withScheme);
  } catch {
    // Not something URL can read: a space in it, or a scheme with nothing after.
    return null;
  }
  if (url.protocol !== "http:" && url.protocol !== "https:") return null;
  // A name, an address, or a bracketed IPv6 one. Chrome's URL lets a space through as %20 where the standard refuses it.
  if (!/^(\[[0-9a-f:.]+\]|[a-z0-9._-]+)$/i.test(url.hostname)) return null;
  return url.origin;
}

/** The API's root: under the configured host, or the page's own /api without one. */
export function apiBase(host: string | null): string {
  return host == null ? "/api" : `${host}/api`;
}

/** What the socket URL needs of the page's address. */
export interface PageAddress {
  readonly protocol: string;
  readonly host: string;
}

/**
 * The screen's WebSocket (client-app §5.3): on the configured host, secure
 * when the host is; on the page's own address when the host serves the page.
 */
export function socketUrl(host: string | null, page: PageAddress, socketPath: string): string {
  if (host == null) return `${page.protocol === "https:" ? "wss" : "ws"}://${page.host}${socketPath}`;
  const origin = new URL(host);
  return `${origin.protocol === "https:" ? "wss" : "ws"}://${origin.host}${socketPath}`;
}

/** The kept address, if it still reads as one. */
export function readHost(storage: Storage): string | null {
  const kept = storage.getItem(HOST_KEY);
  return kept == null ? null : normaliseHost(kept);
}

export function writeHost(storage: Storage, host: string | null): void {
  if (host == null) storage.removeItem(HOST_KEY);
  else storage.setItem(HOST_KEY, host);
}

/** What the shell puts on the page: Capacitor's bridge, which says so. */
export interface ShellWindow {
  readonly Capacitor?: {
    readonly isNativePlatform?: () => boolean;
  };
}

/** Whether this page is the packaged app rather than a browser's tab; the shell's bridge is the tell. */
export function inShell(win: ShellWindow): boolean {
  return win.Capacitor?.isNativePlatform?.() === true;
}

/** The page's own answers, for the modules that run in a window. */
export const shell = (): boolean => typeof window !== "undefined" && inShell(window as ShellWindow);

/** What the bundled shell serves its page from: Capacitor's own origin, `http://localhost` (client-app §8.1). */
export const BUNDLED_HOSTNAME = "localhost";

/**
 * The shell carrying its own copy of the page, which therefore needs to be
 * told where the host is. A shell that loads the hosted app from the network
 * instead (capacitor.config.json `server.url`) is on the host's own origin,
 * calls `/api` there like a browser tab, and has no address to keep.
 */
export function inBundledShell(win: ShellWindow, hostname: string): boolean {
  return inShell(win) && hostname === BUNDLED_HOSTNAME;
}

export const bundledShell = (): boolean => typeof window !== "undefined" && inBundledShell(window as ShellWindow, window.location.hostname);

export function configuredHost(): string | null {
  if (typeof localStorage === "undefined") return null;
  try {
    return readHost(localStorage);
  } catch {
    // Storage can be off (a private window, a policy); then there is no kept host, which is answer enough.
    return null;
  }
}

/** The bundled app with no host yet: nothing to show but the connect screen. */
export const needsHost = (): boolean => bundledShell() && configuredHost() == null;
